import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import type {
  DictaphoneDeviceMapping,
  DictaphoneKeyMappingInput,
  Mutation,
  MutationUpdateDictaphoneDeviceMappingArgs,
} from 'generated/graphql';
import type { DictaphoneButton, DictaphoneKeyMapping, DictaphoneProduct } from './products/types';
import type { UseDictaphoneButtonsParams } from './useDictaphoneProduct';
import { useDictaphoneProduct } from './useDictaphoneProduct';
import { resolveDeviceFingerprintForUpdate } from 'domains/reporter/Reporter/DictationTab/ProgrammableMicSettings.utils';
import { useToolPreferences } from 'hooks/usePreferences';
import { GET_TOOL_PREFERENCES, UPDATE_DICTAPHONE_DEVICE_MAPPING } from 'modules/Apollo/queries';
import { logger } from 'modules/logger';

export type UseDictaphoneKeyMappingResult = {
  dictaphoneProduct: DictaphoneProduct | undefined;
  dictaphoneButtons: ReadonlyArray<DictaphoneButton>;
  deviceMapping: DictaphoneDeviceMapping | undefined;
  keyMapping: DictaphoneKeyMapping | undefined;
  submitDeviceMapping: (keyMapping: ReadonlyArray<DictaphoneKeyMappingInput>) => Promise<void>;
};

type UpdateDictaphoneDeviceMappingMutation = Pick<Mutation, 'updateDictaphoneDeviceMapping'>;

const toKeyMapping = (
  deviceMapping: DictaphoneDeviceMapping | undefined
): DictaphoneKeyMapping | undefined => {
  if (deviceMapping == null) {
    return undefined;
  }

  // Treat empty mappings as an explicit "unmapped" override so defaults do not reapply if I unassign all keys
  if (deviceMapping.keyMapping.length === 0) {
    return {};
  }
  return Object.fromEntries(
    deviceMapping.keyMapping.map(({ key, actionId }) => [key, actionId])
  ) as DictaphoneKeyMapping;
};

export const useDictaphoneKeyMapping = ({
  context,
}: UseDictaphoneButtonsParams): UseDictaphoneKeyMappingResult => {
  const { dictaphoneProduct, dictaphoneButtons, audioInputDeviceLabel } = useDictaphoneProduct({
    context,
  });
  const [toolPreferences] = useToolPreferences({ context });
  const userDeviceMappings = toolPreferences?.dictaphone?.deviceMappings;
  const [updateDictaphoneDeviceMapping] = useMutation<
    UpdateDictaphoneDeviceMappingMutation,
    MutationUpdateDictaphoneDeviceMappingArgs
  >(UPDATE_DICTAPHONE_DEVICE_MAPPING, { context });

  const matchingUserDeviceMapping = useMemo(() => {
    if (!dictaphoneProduct || !userDeviceMappings?.length) return undefined;

    return userDeviceMappings.find(({ deviceFingerprint }) =>
      dictaphoneProduct.matchesDeviceFingerprint(deviceFingerprint)
    );
  }, [dictaphoneProduct, userDeviceMappings]);

  const keyMapping = useMemo(
    () => toKeyMapping(matchingUserDeviceMapping) ?? dictaphoneProduct?.defaultKeyMapping,
    [dictaphoneProduct?.defaultKeyMapping, matchingUserDeviceMapping]
  );
  const deviceFingerprint = useMemo(
    () => resolveDeviceFingerprintForUpdate(matchingUserDeviceMapping, audioInputDeviceLabel),
    [audioInputDeviceLabel, matchingUserDeviceMapping]
  );

  const submitDeviceMapping = useCallback(
    async (nextKeyMapping: ReadonlyArray<DictaphoneKeyMappingInput>) => {
      if (deviceFingerprint == null) {
        logger.warn('[useDictaphoneKeyMapping] Missing device fingerprint; skipping update', {
          keyMapping: nextKeyMapping,
        });
        return;
      }

      await updateDictaphoneDeviceMapping({
        variables: {
          deviceFingerprint,
          keyMapping: nextKeyMapping,
        },
        refetchQueries: [{ query: GET_TOOL_PREFERENCES }],
      });
    },
    [deviceFingerprint, updateDictaphoneDeviceMapping]
  );

  return useMemo(
    () => ({
      dictaphoneProduct,
      dictaphoneButtons,
      deviceMapping: matchingUserDeviceMapping,
      keyMapping,
      submitDeviceMapping,
    }),
    [
      dictaphoneProduct,
      dictaphoneButtons,
      keyMapping,
      matchingUserDeviceMapping,
      submitDeviceMapping,
    ]
  );
};
